import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import defaultTemplateImage from "../../assets/images/template-default.png";
import EventService from "../../services/EventService.js";
import TemplateService from "../../services/TemplateService.js";
import { useEventStore } from "../../store/eventStore";
import "../../assets/css/AddDetailsTab.css";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
const DEFAULT_APP_ID = "91f3c6e5-f6bc-427d-b02d-b4fe515ace0b";
const CANVAS_STATE_COMMENT_PREFIX = "CANVAS_STATE_V1:";

const decodeCanvasState = (encoded) => {
  if (!encoded) return null;
  try {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

const extractTemplateBaseSize = (html) => {
  if (!html || !html.trim()) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const container = doc.body.firstElementChild || doc.body;

    const encodedCanvasState =
      container.getAttribute?.("data-canvas-state") ||
      container.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state") ||
      doc.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state");

    const commentMatch = html.match(
      new RegExp(`<!--\\s*${CANVAS_STATE_COMMENT_PREFIX}([A-Za-z0-9+/=]+)\\s*-->`),
    );
    const encodedFromComment = commentMatch?.[1] || "";
    const decodedCanvasState =
      decodeCanvasState(encodedCanvasState) || decodeCanvasState(encodedFromComment);

    const baseWidth = Number(decodedCanvasState?.baseWidth) || 0;
    const baseHeight = Number(decodedCanvasState?.baseHeight) || 0;
    if (baseWidth > 0 && baseHeight > 0) {
      return { width: baseWidth, height: baseHeight };
    }

    const parseAbsoluteDimension = (rawValue) => {
      if (typeof rawValue !== "string") return 0;
      const trimmed = rawValue.trim();
      if (!trimmed || trimmed.endsWith("%")) return 0;
      const numeric = parseFloat(trimmed);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
    };

    const rootWidth = parseAbsoluteDimension(container.style?.width || "");
    const rootHeight = parseAbsoluteDimension(container.style?.height || "");
    if (
      Number.isFinite(rootWidth) &&
      Number.isFinite(rootHeight) &&
      rootWidth > 0 &&
      rootHeight > 0
    ) {
      return { width: rootWidth, height: rootHeight };
    }
  } catch (error) {
    return null;
  }

  return null;
};

const hasSerializedCanvasState = (html = "") =>
  html.includes("data-canvas-state=") || html.includes(CANVAS_STATE_COMMENT_PREFIX);

const PreviewTab = ({ onNext, onBack }) => {
  const mode = useEventStore((state) => state.mode);
  const campaignData = useEventStore((state) => state.eventDetails);
  const updateEventDetails = useEventStore((state) => state.updateEventDetails);
  const markSaved = useEventStore((state) => state.markSaved);
  const {
    campaignId,
    campaignName,
    startDate,
    endDate,
    eventTime,
    description,
    formFields,
    eventPriceTagLine,
    eventPrice,
    templateId,
    eventTemplateId,
    templateCategory,
    templateImage,
    templateHtml,
    templateSummary,
    videoConferenceLink,
    isDraft,
  } = campaignData;

  // Published/upcoming events opened via /create-events/:id — primary action is update, not first-time create.
  const isUpdatingPublishedEvent = mode === "edit" && !isDraft;

  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(false);
  const bannerRef = useRef(null);
  const templateBodyRef = useRef(null);
  const templateBaseSize = useMemo(
    () => extractTemplateBaseSize(templateHtml),
    [templateHtml],
  );
  const bannerWidth = templateBaseSize?.width || imageSize.width || 900;
  const bannerHeight = templateBaseSize?.height || imageSize.height || 400;

  // Convert UTC to 12-hour IST format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "10:30 AM";
    const [hoursStr, minutes] = time24.split(":");
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  // 🖼️ Load banner image dimensions
  useEffect(() => {
    const img = new Image();
    const src = templateImage
      ? templateImage.startsWith("http")
        ? templateImage
        : `${BASE_URL}${templateImage}`
      : defaultTemplateImage;

    img.src = src;
    img.onload = () =>
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => setImageSize({ width: 900, height: 400 });
  }, [templateImage]);

  // Ensure text doesn't overflow in shapes and auto-adjust font size
  useEffect(() => {
    if (!templateBodyRef.current) return;
    
    const container = templateBodyRef.current;
    const shapes = container.querySelectorAll('div[style*="position: absolute"]');
    
    shapes.forEach((shape) => {
      // Ensure shape container clips content and centers content
      if (!shape.style.overflow || shape.style.overflow === 'visible') {
        shape.style.overflow = 'hidden';
      }
      
      // Ensure shape container uses flexbox for centering
      shape.style.display = 'flex';
      shape.style.alignItems = 'center';
      shape.style.justifyContent = 'center';

      // Backward-compat fix: older saved circles used equal width/height % values,
      // which render as ovals when container width/height differ.
      const widthPercent = shape.style.width?.endsWith('%') ? parseFloat(shape.style.width) : NaN;
      const heightPercent = shape.style.height?.endsWith('%') ? parseFloat(shape.style.height) : NaN;
      const isLegacyCircleCandidate =
        shape.style.borderRadius === '50%' &&
        !shape.style.clipPath &&
        !shape.style.webkitClipPath &&
        Number.isFinite(widthPercent) &&
        Number.isFinite(heightPercent) &&
        Math.abs(widthPercent - heightPercent) < 0.1;

      if (
        !hasSerializedCanvasState(templateHtml) &&
        isLegacyCircleCandidate &&
        container.clientWidth > 0 &&
        container.clientHeight > 0
      ) {
        const correctedHeightPercent = (widthPercent * container.clientWidth) / container.clientHeight;
        shape.style.height = `${correctedHeightPercent}%`;
      }
      
      // Get shape dimensions
      const shapeRect = shape.getBoundingClientRect();
      const shapeWidth = shapeRect.width;
      const shapeHeight = shapeRect.height;
      
      // Ensure text spans don't overflow and auto-adjust font size
      const spans = shape.querySelectorAll('span');
      spans.forEach((span) => {
        span.style.overflow = 'hidden';
        span.style.textOverflow = 'ellipsis';
        span.style.maxWidth = '100%';
        span.style.maxHeight = '100%';
        span.style.display = 'block';
        span.style.wordWrap = 'break-word';
        span.style.wordBreak = 'break-word';
        span.style.textAlign = 'center';
        
        // Auto-adjust font size if text overflows
        if (shapeWidth > 0 && shapeHeight > 0) {
          let fontSize = parseFloat(span.style.fontSize) || parseFloat(window.getComputedStyle(span).fontSize) || 24;
          const originalFontSize = fontSize;
          const minFontSize = 8;
          const padding = 10; // Account for padding/margins
          const availableWidth = shapeWidth - padding;
          const availableHeight = shapeHeight - padding;
          
          // Create a temporary element to measure text with word wrap
          const tempSpan = span.cloneNode(true);
          tempSpan.style.visibility = 'hidden';
          tempSpan.style.position = 'absolute';
          tempSpan.style.top = '-9999px';
          tempSpan.style.left = '-9999px';
          tempSpan.style.width = `${availableWidth}px`;
          tempSpan.style.whiteSpace = 'normal';
          tempSpan.style.wordWrap = 'break-word';
          tempSpan.style.wordBreak = 'break-word';
          document.body.appendChild(tempSpan);
          
          // Reduce font size until text fits
          while (fontSize > minFontSize) {
            tempSpan.style.fontSize = `${fontSize}px`;
            const textHeight = tempSpan.scrollHeight;
            
            // Check if text fits within available height
            if (textHeight <= availableHeight) {
              break;
            }
            
            fontSize = Math.max(minFontSize, fontSize * 0.9); // Reduce by 10%
          }
          
          document.body.removeChild(tempSpan);
          
          // Apply the adjusted font size if it was reduced
          if (fontSize < originalFontSize) {
            span.style.fontSize = `${fontSize}px`;
          }
        }
      });
    });
  }, [templateHtml, imageSize.width, imageSize.height]);

  // ✅ Save Draft
  const handleSave = async () => {
    if (
      !campaignName ||
      !description ||
      !startDate ||
      !endDate ||
      !eventTime ||
      !eventPriceTagLine ||
      !eventPrice ||
      !formFields?.length
    ) {
      alert("Please fill in all required fields before saving.");
      return;
    }

    const payload = {
      eventId: campaignId,
      eventName: campaignName,
      eventDetails: description,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      isPreview: false,
      isDraft: isDraft,
      eventTime: formatTimeTo12Hour(eventTime),
      eventPriceTagLine,
      eventEnterPrice: parseFloat(eventPrice) || 0,
      eventObjective: templateCategory,
      templateId,
      eventTemplateId,
      templateSummary,
      templateBody: templateHtml,
      templateImage,
      eventInputs: formFields.map((f) => ({
        inputTitle: f.label,
        isRequire: f.required,
      })),
      meetingLink: videoConferenceLink,
    };

    try {
      debugger;
      const response = await EventService.saveEvent(payload);
      if (response.data?.success) {
        const ev = response.data.data;
        updateEventDetails(
          {
            campaignId: ev?.id || campaignId,
            templateId: ev?.eventTemplate?.templateId || templateId,
            eventTemplateId: ev?.eventTemplate?.id || eventTemplateId,
            templateHtml: ev?.eventTemplate?.templateBody || templateHtml,
            templateSummary: ev?.eventTemplate?.templateSummary || templateSummary,
            templateCategory: templateCategory,
            templateImage: ev?.eventTemplate?.templateImage || templateImage,
            isDraft: ev?.isDraft ?? isDraft,
          },
          false,
        );
        alert("✅ Event saved successfully!");
        markSaved();
      } else alert("❌ Failed to save event.");
    } catch (error) {
      console.error("Request failed:", error);
      alert("An error occurred while saving.");
    }
  };

  // Create Event — using html-to-image
  const handleCreate = async () => {
    if (
      !campaignName ||
      !description ||
      !startDate ||
      !endDate ||
      !eventTime ||
      !eventPriceTagLine ||
      !eventPrice ||
      !formFields?.length
    ) {
      alert(
        isUpdatingPublishedEvent
          ? "Please fill in all required fields before updating the event."
          : "Please fill in all required fields before creating the event.",
      );
      return;
    }

    setLoading(true);
    try {
      const node = bannerRef.current;
      if (!node) throw new Error("Banner element not found.");

      // generate a sharp PNG from the banner
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 3, // higher ratio = sharper image
        quality: 1,
        backgroundColor: "#ffffff",
      });

      // Convert base64 to Blob
      const blob = await fetch(dataUrl).then((r) => r.blob());

      const fileName = `${campaignName.replace(/\s+/g, "_")}_${new Date()
        .toISOString()
        .slice(0, 10)}.png`;

      const formData = new FormData();
      formData.append("TemplateImage", blob, fileName);
      formData.append("AppId", DEFAULT_APP_ID);

      const uploadResp = await TemplateService.createEventImage(formData);
      const uploadedImagePath =
        uploadResp.data?.filePath || templateImage || "";

      const payload = {
        eventId: campaignId,
        eventName: campaignName,
        eventDetails: description,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        isPreview: false,
        isDraft: false,
        eventTime: formatTimeTo12Hour(eventTime),
        eventPriceTagLine,
        eventEnterPrice: parseFloat(eventPrice) || 0,
        eventObjective: templateCategory,
        templateId,
        eventTemplateId,
        templateSummary,
        templateBody: templateHtml,
        templateImage: uploadedImagePath,
        eventInputs: formFields.map((f) => ({
          inputTitle: f.label,
          isRequire: f.required,
        })),
        meetingLink: videoConferenceLink,
      };
      const response = await EventService.saveEvent(payload);
      if (response.data?.success) {
        const ev = response.data.data;
        updateEventDetails(
          {
            campaignId: ev?.id || campaignId,
            templateId: ev?.eventTemplate?.templateId || templateId,
            eventTemplateId: ev?.eventTemplate?.id || eventTemplateId,
            templateHtml: ev?.eventTemplate?.templateBody || templateHtml,
            templateSummary: ev?.eventTemplate?.templateSummary || templateSummary,
            templateCategory: templateCategory,
            templateImage: ev?.eventTemplate?.templateImage || templateImage,
            isDraft: ev?.isDraft ?? false,
          },
          false,
        );
        alert(
          isUpdatingPublishedEvent
            ? "✅ Event updated successfully!"
            : "🎉 Event created successfully!",
        );
        markSaved();
        onNext();
      } else {
        alert(
          isUpdatingPublishedEvent
            ? "Failed to update event."
            : "Failed to create event.",
        );
      }
    } catch (error) {
      console.error("❌ Error creating event:", error);
      alert(
        isUpdatingPublishedEvent
          ? "An error occurred while updating the event."
          : "An error occurred while creating the event.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="event-campaign-container preview-wrapper"
      style={{ maxWidth: "none", width: "fit-content" }}
    >
      {/* === Banner === */}
      <div
        className="event-banner"
        ref={bannerRef}
        style={{
          width: `${bannerWidth}px`,
          maxWidth: `${bannerWidth}px`,
          height: `${bannerHeight}px`,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={templateBodyRef}
          className="event-template"
          dangerouslySetInnerHTML={{ __html: templateHtml }}
          style={{
            backgroundImage: `url(${
              templateImage
                ? templateImage.startsWith("http")
                  ? templateImage
                  : `${BASE_URL}${templateImage}`
                : defaultTemplateImage
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: `${bannerWidth}px`,
            maxWidth: `${bannerWidth}px`,
            height: `${bannerHeight}px`,
            position: "relative",
            padding: 0,
            margin: 0,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        ></div>
      </div>

      {/* === Description === */}
      <div
        className="event-description aligned-section"
        style={{ width: `${bannerWidth}px`, maxWidth: `${bannerWidth}px` }}
      >
        {description && (
          <div
            className="event-desc-text"
            dangerouslySetInnerHTML={{ __html: description }}
          ></div>
        )}
        {eventPriceTagLine && (
          <div className="event-price-tag">
            <p>
              {eventPriceTagLine}{" "}
              <span style={{ color: "red" }}>₹ {eventPrice}</span>
            </p>
          </div>
        )}
        {videoConferenceLink && (
          <div className="event-conference-link">
            <p>
              <strong>Video Conference Link:</strong>{" "}
              <a
                href={videoConferenceLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {videoConferenceLink}
              </a>
            </p>
          </div>
        )}
      </div>

      {/* === Registration Form === */}
      <form
        className="register-section aligned-section"
        style={{ width: `${bannerWidth}px`, maxWidth: `${bannerWidth}px` }}
      >
        <h4>Register</h4>
        <div className="form-fields">
          {formFields.length === 0 ? (
            <p className="text-muted">No fields added.</p>
          ) : (
            formFields.map((field, idx) => (
              <div className="form-group" key={idx}>
                <label>
                  {field.label}
                  {field.required && <span className="required-star">*</span>}
                </label>
                <input
                  type="text"
                  placeholder={field.label}
                  className="input-field"
                  required={field.required}
                />
              </div>
            ))
          )}
        </div>
      </form>

      {/* === Buttons === */}
      <div className="action-buttons">
        <button className="btn secondary go-back" onClick={onBack}>
          Back
        </button>
        <div className="right-actions">
          <button className="btn warn save" onClick={handleSave} disabled={!isDraft}>
            Save as Draft
          </button>
          <button
            className="btn primary save"
            onClick={handleCreate}
            disabled={loading}
          >
            {loading
              ? isUpdatingPublishedEvent
                ? "Updating..."
                : "Creating..."
              : isUpdatingPublishedEvent
                ? "Update Event"
                : "Create Event"}
          </button>
          <button className="btn success continue" onClick={onNext}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewTab;
